import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
    format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
    isSameMonth, isToday, addMonths, subMonths,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    CalendarCheck, ChevronLeft, ChevronRight, Plus, X, Save, Trash2, Loader2,
    Phone, Mail, Clock, User, Building2, Pencil, Bell, Download, FileText, Users as UsersIcon, Settings as SettingsIcon, ExternalLink, CalendarX2,
} from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { generateDemarcheAnswersPdf } from '../../lib/demarcheAnswersPdfGenerator';
import { formatDateFr } from '../../lib/formatDate';
import { SendDocumentByEmailModal } from './SendDocumentByEmailModal';
import { RdvCompletionModal } from './RdvCompletionModal';
import { ProRdvHoursTab } from '../Settings/SettingsPage';
import { useAuth } from '../../context/AuthContext';
import { TokenPicker } from '../ui/TokenPicker';

const CONFIRMATION_EMAIL_TOKENS = [
    { token: '{{prenom}}', desc: 'Prénom du client' },
    { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
    { token: '{{entreprise}}', desc: "Nom de l'entreprise" },
    { token: '{{date}}', desc: 'Date du rendez-vous' },
    { token: '{{heure}}', desc: 'Heure du rendez-vous' },
    { token: '{{logo}}', desc: "Logo de l'entreprise" },
    { token: '{{personne_assignee}}', desc: 'Personne assignée au rendez-vous' },
];

interface ResponseChoice { label: string; qty: number; }
interface CustomAnswer { id: string; question: string; type: 'text' | 'select'; answer: string; }

interface RdvAppointment {
    id: string;
    crm_contact_id: string | null;
    /** Le référent de la fiche CRM du client — repris tel quel dans le PDF des réponses. */
    contact_organizer_name?: string;
    contact_organizer_email?: string;
    contact_organizer_phone?: string;
    document_request_id: string | null;
    template_id: string | null;
    subject: string;
    notes: string;
    internal_note: string;
    assigned_note: string;
    employee_ids: string[];
    appointment_date: string;
    start_time: string;
    end_time: string;
    status: 'en_attente' | 'confirme' | 'refuse' | 'annule' | 'termine';
    refuse_reason?: string;
    total_amount_ttc?: number;
    amount_already_paid?: number;
    payment_status?: 'a_encaisser' | 'encaisse';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    contact_type?: string;
    contact_phone?: string;
    contact_email?: string;
    document_name?: string;
    attachment_name?: string;
    has_attachment?: boolean;
    response_choices?: ResponseChoice[];
    response_text?: string;
    custom_answers?: CustomAnswer[];
    document_request_status?: 'pending' | 'responded' | 'completed';
    video_link?: string;
}

interface Employee { id: string; first_name?: string; last_name?: string; }

interface CrmSuggestion {
    id: string; type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string; last_name?: string; company_name?: string;
    phone?: string; email?: string; address?: string; postal_code?: string; city?: string;
}

const contactLabel = (a: RdvAppointment) => a.contact_type === 'PROFESSIONNEL'
    ? (a.company_name || 'Contact')
    : `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Contact';

const employeeName = (e: Employee) => `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Salarié';

const inputCls = "w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors";
const labelCls = "text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block";

const STATUS_BADGE: Record<RdvAppointment['status'], { label: string; cls: string }> = {
    en_attente: { label: 'En attente', cls: 'bg-warning-500/10 text-warning-500 border-warning-500/20' },
    confirme: { label: 'Confirmé', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    refuse: { label: 'Refusé', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
    annule: { label: 'Annulé', cls: 'bg-surface-soft text-content-muted border-border' },
    termine: { label: 'Terminé', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
};

const fmtEuro = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

function RdvDetailModal({
    appt, employees, onClose, onEdit, onCancelled, onDecided,
}: {
    appt: RdvAppointment;
    employees: Employee[];
    onClose: () => void;
    onEdit: () => void;
    onCancelled: () => void;
    onDecided: () => void;
}) {
    const [cancelling, setCancelling] = React.useState(false);
    const [reminding, setReminding] = React.useState(false);
    const [reminderSent, setReminderSent] = React.useState(false);
    const [error, setError] = React.useState('');
    const [pendingEmployeeIds, setPendingEmployeeIds] = React.useState<string[]>(appt.employee_ids);
    const [pendingAssignedNote, setPendingAssignedNote] = React.useState(appt.assigned_note || '');
    const [pendingVideoLink, setPendingVideoLink] = React.useState(appt.video_link || '');
    const [accepting, setAccepting] = React.useState(false);
    const [refusing, setRefusing] = React.useState(false);
    const [showRefuseForm, setShowRefuseForm] = React.useState(false);
    const [refuseReason, setRefuseReason] = React.useState('');
    const [showCompletion, setShowCompletion] = React.useState(false);

    const totalDue = Number(appt.total_amount_ttc) || 0;
    const totalPaid = Number(appt.amount_already_paid) || 0;
    const remainingDue = Math.max(0, totalDue - totalPaid);

    const assignedEmployees = employees.filter((e) => appt.employee_ids.includes(String(e.id)));
    // RDV créé manuellement avec une date déjà fixée par l'établissement, en attente de la réponse du
    // contact à une Démarche (choix/questions) — le statut "en_attente" ne signifie pas ici "créneau demandé
    // par le contact, à approuver" (flux historique), mais "réponse du contact pas encore reçue" : la
    // confirmation est alors automatique (cf. POST /documentation/:token/respond), jamais manuelle.
    const awaitingResponse = appt.status === 'en_attente' && !!appt.document_request_id && appt.document_request_status === 'pending';
    const togglePendingEmployee = (id: string) => setPendingEmployeeIds((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);

    const handleCancel = async () => {
        if (!confirm('Annuler ce rendez-vous ? Le créneau redeviendra disponible.')) return;
        setCancelling(true);
        try {
            await moduleApi.cancelProRdvAppointment(appt.id);
            onCancelled();
        } finally {
            setCancelling(false);
        }
    };

    const handleRemind = async () => {
        setReminding(true);
        setError('');
        try {
            const res: any = await moduleApi.sendProRdvReminder(appt.id);
            if (res?.error) { setError(res.error); return; }
            setReminderSent(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'envoi.');
        } finally {
            setReminding(false);
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        setError('');
        try {
            const res: any = await moduleApi.acceptProRdvAppointment(appt.id, pendingEmployeeIds, pendingAssignedNote.trim(), pendingVideoLink.trim());
            if (res?.error) { setError(res.error); return; }
            onDecided();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'acceptation.');
        } finally {
            setAccepting(false);
        }
    };

    const handleRefuse = async () => {
        if (!refuseReason.trim()) { setError('Merci d\'indiquer un motif.'); return; }
        setRefusing(true);
        setError('');
        try {
            const res: any = await moduleApi.refuseProRdvAppointment(appt.id, refuseReason.trim());
            if (res?.error) { setError(res.error); return; }
            onDecided();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du refus.');
        } finally {
            setRefusing(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                        <h2 className="text-base font-bold text-content flex items-center gap-2">
                            <CalendarCheck size={17} className="text-brand-500" /> Détail du rendez-vous
                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${STATUS_BADGE[appt.status].cls}`}>{STATUS_BADGE[appt.status].label}</span>
                        </h2>
                        <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                    </div>

                    <div className="p-5 space-y-4 overflow-y-auto">
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1">
                            <p className="text-sm font-bold text-content flex items-center gap-2">
                                {appt.contact_type === 'PROFESSIONNEL' ? <Building2 size={14} className="text-content-muted" /> : <User size={14} className="text-content-muted" />}
                                {contactLabel(appt)}
                            </p>
                            {appt.contact_phone && <p className="text-xs text-content-muted flex items-center gap-1.5"><Phone size={11} /> {appt.contact_phone}</p>}
                            {appt.contact_email && <p className="text-xs text-content-muted flex items-center gap-1.5"><Mail size={11} /> {appt.contact_email}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 rounded-xl bg-surface-soft border border-border">
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date</p>
                                <p className="text-sm font-bold text-content mt-0.5">{new Date(appt.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-surface-soft border border-border">
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Horaire</p>
                                <p className="text-sm font-bold text-content mt-0.5 flex items-center gap-1.5"><Clock size={12} /> {appt.start_time} – {appt.end_time}</p>
                            </div>
                        </div>

                        {appt.subject && (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1">Raison</p>
                                <p className="text-sm text-content">{appt.subject}</p>
                            </div>
                        )}
                        {appt.notes && (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1">Message du client</p>
                                <p className="text-sm text-content whitespace-pre-wrap">{appt.notes}</p>
                            </div>
                        )}
                        {appt.internal_note && (
                            <div className="p-3 rounded-xl bg-warning-500/5 border border-warning-500/20">
                                <p className="text-[10px] font-bold text-warning-500 uppercase tracking-widest mb-1">Message interne (visible uniquement par vous)</p>
                                <p className="text-sm text-content whitespace-pre-wrap">{appt.internal_note}</p>
                            </div>
                        )}

                        {appt.status === 'refuse' && appt.refuse_reason && (
                            <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                                <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest mb-1">Motif du refus</p>
                                <p className="text-sm text-content">{appt.refuse_reason}</p>
                            </div>
                        )}

                        {awaitingResponse && (
                            <div className="p-3 rounded-xl bg-warning-500/5 border border-warning-500/20">
                                <p className="text-sm text-content font-semibold">En attente de la réponse du contact.</p>
                                <p className="text-xs text-content-muted mt-1">Ce rendez-vous se confirmera automatiquement dès que le contact aura répondu au lien qui lui a été envoyé — rien à valider de votre côté.</p>
                            </div>
                        )}

                        {appt.status === 'en_attente' && !awaitingResponse && employees.length > 0 ? (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><UsersIcon size={11} /> Assigner du personnel (optionnel)</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {employees.map((e) => (
                                        <button key={e.id} type="button" onClick={() => togglePendingEmployee(String(e.id))}
                                            className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all ${pendingEmployeeIds.includes(String(e.id)) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                            {employeeName(e)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : appt.status === 'en_attente' && !awaitingResponse && employees.length === 0 ? (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><UsersIcon size={11} /> Personne assignée (optionnel)</p>
                                <input type="text" value={pendingAssignedNote} onChange={(e) => setPendingAssignedNote(e.target.value)}
                                    placeholder="Ex: Moi-même" className={inputCls} />
                            </div>
                        ) : assignedEmployees.length > 0 ? (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><UsersIcon size={11} /> Salariés assignés</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {assignedEmployees.map((e) => (
                                        <span key={e.id} className="px-2.5 py-1 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-bold border border-brand-500/20">{employeeName(e)}</span>
                                    ))}
                                </div>
                            </div>
                        ) : appt.assigned_note && (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><UsersIcon size={11} /> Personne assignée</p>
                                <p className="text-sm text-content">{appt.assigned_note}</p>
                            </div>
                        )}

                        {appt.status === 'en_attente' && !awaitingResponse ? (
                            <div>
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1.5">Lien visio (optionnel)</p>
                                <input type="url" value={pendingVideoLink} onChange={(e) => setPendingVideoLink(e.target.value)}
                                    placeholder="Ex: https://zoom.us/j/..." className={inputCls} />
                                <p className="text-[11px] text-content-muted mt-1">Inclus dans l'email de confirmation envoyé au contact si vous acceptez ce rendez-vous.</p>
                            </div>
                        ) : appt.video_link && (
                            <a href={appt.video_link} target="_blank" rel="noreferrer"
                                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-border text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 text-sm font-bold transition-all">
                                <ExternalLink size={14} /> Rejoindre la réunion en ligne
                            </a>
                        )}

                        {appt.status === 'en_attente' && !awaitingResponse && showRefuseForm && (
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Motif du refus</label>
                                <textarea value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={2}
                                    placeholder="Ex: Créneau finalement indisponible" className={inputCls} />
                            </div>
                        )}

                        {(appt.document_name || (appt.response_choices && appt.response_choices.length > 0) || appt.response_text || (appt.custom_answers && appt.custom_answers.length > 0)) && (
                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><FileText size={11} /> {appt.document_request_id ? 'Depuis la démarche' : 'Type de rendez-vous'}{appt.document_name ? ` "${appt.document_name}"` : ''}</p>
                                {appt.response_choices && appt.response_choices.length > 0 && (
                                    <p className="text-xs text-content">{appt.response_choices.map((c) => `${c.label} x${c.qty}`).join(', ')}</p>
                                )}
                                {(appt.custom_answers || []).map((a) => (
                                    <p key={a.id} className="text-xs text-content"><span className="text-content-muted">{a.question} :</span> {a.answer}</p>
                                ))}
                                {appt.response_text && <p className="text-xs text-content">{appt.response_text}</p>}
                                {/* Les réponses étaient déjà lisibles ici, mais pas emportables : demande d'Emeni
                                    (2026-08-26) de pouvoir les sortir en PDF depuis le rendez-vous — pour les
                                    imprimer au brief de cuisine, les transmettre à un prestataire, ou les joindre
                                    à un devis établi dans un autre logiciel. Généré à la demande plutôt que
                                    stocké : le fichier serait de toute façon reconstruit à l'identique, et une
                                    copie figée finirait par contredire la fiche si une réponse est corrigée. */}
                                {(appt.custom_answers && appt.custom_answers.length > 0) && (
                                    <button type="button"
                                        onClick={() => generateDemarcheAnswersPdf({
                                            id: appt.id,
                                            contactName: contactLabel(appt),
                                            contactEmail: appt.contact_email,
                                            contactPhone: appt.contact_phone,
                                            // Le référent de la fiche CRM du client : c'est lui qui remplit le
                                            // bloc « Référent contact », jusqu'ici toujours vide.
                                            referentName: appt.contact_organizer_name,
                                            referentEmail: appt.contact_organizer_email,
                                            referentPhone: appt.contact_organizer_phone,
                                            demarcheName: appt.document_name,
                                            // Date en jj/mm/aaaa puis l'heure, jamais la forme brute de la base
                                            // (2026-10-01) : c'est un document qu'on imprime et qu'on transmet.
                                            // Regle d'Emeni (2026-08-29) : le format de date est toujours celui-la.
                                            appointmentLabel: `${formatDateFr(appt.appointment_date)} ${appt.start_time}`.trim(),
                                            answers: (appt.custom_answers || []).map((a) => ({ question: a.question, answer: a.answer })),
                                            choices: (appt.response_choices || []).map((c) => `${c.label} x${c.qty}`),
                                            freeText: appt.response_text,
                                        })}
                                        className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-xs font-bold transition-all">
                                        <Download size={12} /> Réponses en PDF
                                    </button>
                                )}
                                {appt.has_attachment && (
                                    <a href={`/api/pro-rdv/appointments/${appt.id}/attachment`} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-xs font-bold transition-all">
                                        <Download size={12} /> {appt.attachment_name || 'Pièce jointe'}
                                    </a>
                                )}
                            </div>
                        )}

                        {totalDue > 0 && (
                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Paiement</p>
                                <div className="flex justify-between text-content-muted">
                                    <span>Total dû</span>
                                    <span>{fmtEuro(totalDue)}</span>
                                </div>
                                {totalPaid > 0 && (
                                    <div className="flex justify-between text-emerald-500 font-semibold">
                                        <span>{appt.status === 'termine' ? 'Réglé' : 'Déjà réglé en ligne'}</span>
                                        <span>{fmtEuro(totalPaid)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-1.5 border-t border-border font-bold text-content">
                                    <span>{remainingDue > 0 ? 'Reste à encaisser' : 'Statut'}</span>
                                    <span className={remainingDue > 0 ? 'text-red-400' : 'text-emerald-400'}>
                                        {remainingDue > 0 ? fmtEuro(remainingDue) : 'Réglé intégralement'}
                                    </span>
                                </div>
                            </div>
                        )}

                        {reminderSent && <p className="text-xs text-emerald-500 font-semibold">Rappel envoyé.</p>}
                        {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                    </div>

                    {appt.status === 'en_attente' && awaitingResponse ? (
                        <div className="flex flex-wrap justify-between gap-3 p-5 border-t border-border shrink-0">
                            <button onClick={handleCancel} disabled={cancelling}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold text-sm transition-all disabled:opacity-50">
                                <Trash2 size={15} /> {cancelling ? 'Suppression...' : 'Supprimer le créneau'}
                            </button>
                            <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Fermer</button>
                        </div>
                    ) : appt.status === 'en_attente' ? (
                        <div className="flex flex-wrap justify-end gap-3 p-5 border-t border-border shrink-0">
                            {showRefuseForm ? (
                                <>
                                    <button onClick={() => setShowRefuseForm(false)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                                    <button onClick={handleRefuse} disabled={refusing}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm transition-all disabled:opacity-50">
                                        <X size={15} /> {refusing ? 'Envoi...' : 'Confirmer le refus'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => setShowRefuseForm(true)}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold text-sm transition-all">
                                        <X size={15} /> Refuser
                                    </button>
                                    <button onClick={handleAccept} disabled={accepting}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all disabled:opacity-50">
                                        <CalendarCheck size={15} /> {accepting ? 'Envoi...' : 'Accepter'}
                                    </button>
                                </>
                            )}
                        </div>
                    ) : appt.status === 'confirme' ? (
                        <div className="flex flex-wrap justify-between gap-3 p-5 border-t border-border shrink-0">
                            <button onClick={handleCancel} disabled={cancelling}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold text-sm transition-all disabled:opacity-50">
                                <Trash2 size={15} /> {cancelling ? 'Annulation...' : 'Annuler'}
                            </button>
                            <div className="flex gap-3">
                                {appt.contact_email && (
                                    <button onClick={handleRemind} disabled={reminding || reminderSent}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all disabled:opacity-50">
                                        <Bell size={15} /> {reminding ? 'Envoi...' : 'Rappeler le RDV'}
                                    </button>
                                )}
                                <button onClick={onEdit} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
                                    <Pencil size={15} /> Modifier
                                </button>
                                <button onClick={() => setShowCompletion(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all">
                                    <CalendarCheck size={15} /> Terminer
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-end p-5 border-t border-border shrink-0">
                            <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Fermer</button>
                        </div>
                    )}
                </motion.div>
            </div>
            {showCompletion && (
                <RdvCompletionModal
                    appt={appt}
                    onClose={() => setShowCompletion(false)}
                    onDone={() => { setShowCompletion(false); onDecided(); onClose(); }}
                />
            )}
        </AnimatePresence>
    );
}

function RdvFormModal({
    initial, defaultDate, employees, onClose, onSaved,
}: {
    initial: RdvAppointment | null;
    defaultDate: string;
    employees: Employee[];
    onClose: () => void;
    onSaved: () => void;
}) {
    const { user } = useAuth();
    // Le calendrier RDV Pro du super admin ne sert qu'à démarcher de futurs clients professionnels de
    // L'IAmani (jamais de particuliers) — même principe que le widget public (professionals_only côté
    // resolveRdvProBookingOwner). Les contacts de mes clients (Restauration/Prestation), eux, peuvent être
    // particuliers ou professionnels, donc le sélecteur reste affiché pour eux.
    const isAdmin = user?.type === 'admin' || (user as any)?.role === 'superadmin';
    const [crmContactId, setCrmContactId] = React.useState(initial?.crm_contact_id || '');
    const [crmType, setCrmType] = React.useState<'PRIVÉ' | 'PROFESSIONNEL'>(isAdmin ? 'PROFESSIONNEL' : ((initial?.contact_type as any) || 'PRIVÉ'));
    const [firstName, setFirstName] = React.useState(initial?.first_name || '');
    const [lastName, setLastName] = React.useState(initial?.last_name || '');
    const [companyName, setCompanyName] = React.useState(initial?.company_name || '');
    const [phone, setPhone] = React.useState(initial?.contact_phone || '');
    const [email, setEmail] = React.useState(initial?.contact_email || '');
    const [date, setDate] = React.useState(initial?.appointment_date || defaultDate);
    const [startTime, setStartTime] = React.useState(initial?.start_time || '09:00');
    const [subject, setSubject] = React.useState(initial?.subject || '');
    const [videoLink, setVideoLink] = React.useState(initial?.video_link || '');
    const [notes, setNotes] = React.useState(initial?.notes || '');
    const [internalNote, setInternalNote] = React.useState(initial?.internal_note || '');
    const [assignedNote, setAssignedNote] = React.useState(initial?.assigned_note || '');
    const [employeeIds, setEmployeeIds] = React.useState<string[]>(initial?.employee_ids || []);
    const [notifyReschedule, setNotifyReschedule] = React.useState(false);
    const [lookupField, setLookupField] = React.useState<'firstName' | 'lastName' | 'companyName' | null>(null);
    const [lookupTerm, setLookupTerm] = React.useState('');
    const [suggestions, setSuggestions] = React.useState<CrmSuggestion[]>([]);
    const [searching, setSearching] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [rdvTypes, setRdvTypes] = React.useState<{ id: string; name: string; message_subject: string; message_body: string }[]>([]);
    const [templateId, setTemplateId] = React.useState('');
    // Objet/corps de l'email envoyé au contact quand la démarche choisie a des choix/questions configurés
    // (lien "Répondre") — le nom de la démarche s'affiche automatiquement dans l'en-tête de l'email, ces
    // champs ne couvrent que le message. Pré-remplis avec le message déjà configuré sur la démarche dès sa
    // sélection (repris tel quel dans Paramètres > Démarches client), puis librement adaptables pour cet
    // envoi précis. Sans type sélectionné (ou type sans choix/question), aucun de ces champs n'est utilisé :
    // le message générique de Paramètres > Notifications s'applique tel quel.
    const [linkSubject, setLinkSubject] = React.useState('');
    const [linkBody, setLinkBody] = React.useState('');
    const linkBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const selectRdvType = (id: string) => {
        setTemplateId(id);
        if (!id) return;
        // Reprend le message déjà configuré sur cette démarche à chaque sélection (même si un autre type
        // avait été choisi juste avant) — la sélection reflète toujours le contenu paramétré, l'admin
        // l'adapte ensuite librement pour cet envoi précis.
        const t = rdvTypes.find((rt) => rt.id === id);
        setLinkSubject(t?.message_subject?.trim() || '{{entreprise}} — Confirmation de votre rendez-vous');
        setLinkBody(t?.message_body?.trim() || 'Bonjour {{prenom}}, votre rendez-vous est prévu le {{date}} à {{heure}} — merci de compléter les informations ci-dessous.');
    };

    const dateOrTimeChanged = !!initial && (date !== initial.appointment_date || startTime !== initial.start_time);

    React.useEffect(() => {
        if (initial) return;
        moduleApi.getDocumentTemplates()
            .then((d: any) => setRdvTypes((Array.isArray(d) ? d : []).filter((t: any) => t.is_active && t.offer_appointment).map((t: any) => ({ id: t.id, name: t.name, message_subject: t.message_subject || '', message_body: t.message_body || '' }))))
            .catch(() => setRdvTypes([]));
    }, [initial]);

    React.useEffect(() => {
        const q = lookupTerm.trim();
        if (q.length < 2) { setSuggestions([]); setSearching(false); return; }
        let cancelled = false;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const items = await moduleApi.searchCRMContacts(q);
                if (!cancelled) setSuggestions(Array.isArray(items) ? items : []);
            } catch { if (!cancelled) setSuggestions([]); } finally { if (!cancelled) setSearching(false); }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [lookupTerm]);

    const triggerLookup = (field: 'firstName' | 'lastName' | 'companyName', value: string) => {
        setLookupField(field);
        setLookupTerm(value);
    };

    const applyCRMContact = (s: CrmSuggestion) => {
        if (s.type === 'PRIVÉ' || s.type === 'PROFESSIONNEL') setCrmType(s.type);
        setFirstName(s.first_name || '');
        setLastName(s.last_name || '');
        setCompanyName(s.company_name || '');
        setPhone(s.phone || '');
        setEmail(s.email || '');
        setCrmContactId(s.id);
        setSuggestions([]);
        setLookupTerm('');
        setLookupField(null);
    };

    const renderSuggestions = (field: 'firstName' | 'lastName' | 'companyName') => {
        if (lookupField !== field) return null;
        if (!searching && suggestions.length === 0) return null;
        return (
            <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-xl shadow-xl max-h-48 overflow-y-auto">
                {searching && <div className="px-3 py-2 text-[11px] text-content-muted">Recherche CRM...</div>}
                {!searching && suggestions.map((s) => (
                    <button key={s.id} type="button" onMouseDown={() => applyCRMContact(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-surface-soft transition-colors text-content">
                        <div className="font-semibold truncate">{s.type === 'PROFESSIONNEL' ? (s.company_name || 'Entreprise') : [s.first_name, s.last_name].filter(Boolean).join(' ')}</div>
                        {s.phone && <div className="text-[11px] text-content-muted">{s.phone}</div>}
                    </button>
                ))}
            </div>
        );
    };

    const toggleEmployee = (id: string) => {
        setEmployeeIds((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);
    };

    const handleSave = async () => {
        setError('');
        if (!phone.trim()) { setError('Le téléphone est requis.'); return; }
        if (!firstName.trim() && !lastName.trim() && !companyName.trim()) { setError('Renseignez le contact (nom ou société).'); return; }
        if (!date || !startTime) { setError('Date et heure requises.'); return; }
        setSaving(true);
        try {
            if (initial) {
                const res: any = await moduleApi.rescheduleProRdvAppointment(initial.id, {
                    date, start_time: startTime, subject, notes, internal_note: internalNote, employee_ids: employeeIds,
                    notify_reschedule: dateOrTimeChanged && notifyReschedule, video_link: videoLink.trim(),
                });
                if (res?.error) { setError(res.error); return; }
            } else {
                const res: any = await moduleApi.createProRdvAppointment({
                    crm_contact_id: crmContactId || undefined, crm_type: crmType,
                    first_name: firstName, last_name: lastName, company_name: companyName, phone, email,
                    date, start_time: startTime, subject, notes, internal_note: internalNote, employee_ids: employeeIds,
                    template_id: templateId || undefined,
                    link_subject: linkSubject.trim() || undefined,
                    link_body: linkBody.trim() || undefined,
                    video_link: videoLink.trim() || undefined,
                });
                if (res?.error) { setError(res.error); return; }
            }
            onSaved();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                        <h2 className="text-base font-bold text-content flex items-center gap-2"><CalendarCheck size={17} className="text-brand-500" /> {initial ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}</h2>
                        <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                    </div>

                    <div className="p-5 space-y-4 overflow-y-auto">
                        {!initial && (
                            <>
                                {!isAdmin && (
                                    <div>
                                        <label className={labelCls}>Type de contact</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {(['PRIVÉ', 'PROFESSIONNEL'] as const).map((t) => (
                                                <button key={t} type="button" onClick={() => setCrmType(t)}
                                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${crmType === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                    {t === 'PRIVÉ' ? 'Particulier' : 'Professionnel'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {crmType === 'PROFESSIONNEL' ? (
                                    <div className="relative">
                                        <label className={labelCls}>Société</label>
                                        <input type="text" value={companyName} onChange={(e) => { setCompanyName(e.target.value); triggerLookup('companyName', e.target.value); }}
                                            onFocus={() => setLookupField('companyName')} className={inputCls} />
                                        {renderSuggestions('companyName')}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="relative">
                                            <label className={labelCls}>Prénom</label>
                                            <input type="text" value={firstName} onChange={(e) => { setFirstName(e.target.value); triggerLookup('firstName', e.target.value); }}
                                                onFocus={() => setLookupField('firstName')} className={inputCls} />
                                            {renderSuggestions('firstName')}
                                        </div>
                                        <div className="relative">
                                            <label className={labelCls}>Nom</label>
                                            <input type="text" value={lastName} onChange={(e) => { setLastName(e.target.value); triggerLookup('lastName', e.target.value); }}
                                                onFocus={() => setLookupField('lastName')} className={inputCls} />
                                            {renderSuggestions('lastName')}
                                        </div>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelCls}>Téléphone</label>
                                        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Email</label>
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </>
                        )}
                        {initial && (
                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1">
                                <p className="text-sm font-bold text-content flex items-center gap-2">
                                    {initial.contact_type === 'PROFESSIONNEL' ? <Building2 size={14} className="text-content-muted" /> : <User size={14} className="text-content-muted" />}
                                    {contactLabel(initial)}
                                </p>
                                {initial.contact_phone && <p className="text-xs text-content-muted flex items-center gap-1.5"><Phone size={11} /> {initial.contact_phone}</p>}
                                {initial.contact_email && <p className="text-xs text-content-muted flex items-center gap-1.5"><Mail size={11} /> {initial.contact_email}</p>}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Date</label>
                                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Heure</label>
                                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} />
                            </div>
                        </div>

                        {!initial && rdvTypes.length > 0 && (
                            <div>
                                <label className={labelCls}>Type de rendez-vous (optionnel)</label>
                                <select value={templateId} onChange={(e) => selectRdvType(e.target.value)} className={inputCls}>
                                    <option value="">Aucun (durée par défaut)</option>
                                    {rdvTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                                <p className="text-[11px] text-content-muted mt-1">Sert à reprendre la durée de ce type. Sans choix ni question configurés dessus, le contact reçoit la confirmation générique de Paramètres &gt; Notifications.</p>
                            </div>
                        )}

                        {!initial && templateId && (
                            <div className="space-y-1.5">
                                <label className={labelCls}>Email envoyé si des choix/questions sont configurés sur ce type</label>
                                <p className="text-[11px] text-content-muted -mt-1">Le nom de la démarche s'affiche automatiquement dans l'en-tête de l'email — objet et message restent à votre charge, réadaptables à chaque envoi.</p>
                                <input type="text" value={linkSubject} onChange={(e) => setLinkSubject(e.target.value)} className={inputCls} placeholder="Objet" />
                                <textarea ref={linkBodyRef} value={linkBody} onChange={(e) => setLinkBody(e.target.value)} rows={4}
                                    className={`${inputCls} resize-none`} placeholder="Message" />
                                <TokenPicker tokens={CONFIRMATION_EMAIL_TOKENS} textareaRef={linkBodyRef} value={linkBody} onChange={setLinkBody} />
                            </div>
                        )}

                        {initial && dateOrTimeChanged && initial.contact_email && (
                            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-warning-500/10 border border-warning-500/20 cursor-pointer">
                                <input type="checkbox" checked={notifyReschedule} onChange={(e) => setNotifyReschedule(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                                <span className="text-xs font-bold text-content">Envoyer une notification de mise à jour au client</span>
                            </label>
                        )}

                        {initial && (
                            <>
                                <div>
                                    <label className={labelCls}>Raison (optionnel)</label>
                                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="Ex: Visite pour privatisation" />
                                </div>
                                <div>
                                    <label className={labelCls}>Message (optionnel)</label>
                                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={`${inputCls} resize-none`} />
                                </div>
                            </>
                        )}
                        <div>
                            <label className={labelCls}>Message interne (optionnel)</label>
                            <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} className={`${inputCls} resize-none`}
                                placeholder="Note visible uniquement par vous — jamais envoyée au client" />
                        </div>
                        <div>
                            <label className={labelCls}>Lien visio (optionnel)</label>
                            <input type="url" value={videoLink} onChange={(e) => setVideoLink(e.target.value)} className={inputCls} placeholder="Ex: https://zoom.us/j/..." />
                            <p className="text-[11px] text-content-muted mt-1">Inclus automatiquement dans l'email de confirmation envoyé au contact, dès qu'il est renseigné.</p>
                        </div>

                        {employees.length > 0 ? (
                            <div>
                                <label className={labelCls}>Salariés assignés (optionnel)</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {employees.map((e) => (
                                        <button key={e.id} type="button" onClick={() => toggleEmployee(String(e.id))}
                                            className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all ${employeeIds.includes(String(e.id)) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                            {employeeName(e)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className={labelCls}>Personne assignée (optionnel)</label>
                                <input type="text" value={assignedNote} onChange={(e) => setAssignedNote(e.target.value)} className={inputCls} placeholder="Ex: Moi-même" />
                            </div>
                        )}

                        {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                    </div>

                    <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                        <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50">
                            <Save size={15} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

export const RdvProModule: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = React.useState(new Date());
    const [appointments, setAppointments] = React.useState<RdvAppointment[]>([]);
    const [employees, setEmployees] = React.useState<Employee[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showDetail, setShowDetail] = React.useState<RdvAppointment | null>(null);
    const [showForm, setShowForm] = React.useState(false);
    const [editing, setEditing] = React.useState<RdvAppointment | null>(null);
    const [defaultDate, setDefaultDate] = React.useState('');
    const [pending, setPending] = React.useState<RdvAppointment[]>([]);
    const [recentlyConfirmed, setRecentlyConfirmed] = React.useState<RdvAppointment[]>([]);
    const [showPendingList, setShowPendingList] = React.useState(false);
    const [showSendDoc, setShowSendDoc] = React.useState(false);
    const [showSettings, setShowSettings] = React.useState(false);
    const [docHistory, setDocHistory] = React.useState<any[]>([]);

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    const load = React.useCallback(() => {
        setLoading(true);
        const from = format(gridStart, 'yyyy-MM-dd');
        const to = format(gridEnd, 'yyyy-MM-dd');
        moduleApi.getProRdvAppointments(from, to)
            .then((d: any) => setAppointments(Array.isArray(d) ? d : []))
            .catch(() => setAppointments([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [format(gridStart, 'yyyy-MM-dd'), format(gridEnd, 'yyyy-MM-dd')]);

    React.useEffect(() => { load(); }, [load]);

    const loadPending = React.useCallback(() => {
        moduleApi.getProRdvPending().then((d: any) => {
            setPending(Array.isArray(d?.pending) ? d.pending : []);
            setRecentlyConfirmed(Array.isArray(d?.recently_confirmed) ? d.recently_confirmed : []);
        }).catch(() => { setPending([]); setRecentlyConfirmed([]); });
    }, []);

    React.useEffect(() => { loadPending(); }, [loadPending]);

    // "Demandes récurrentes" : historique des Démarches client envoyées, accessible directement depuis la
    // cloche de RDV Pro (avec les demandes de RDV en attente) — c'était la demande initiale du propriétaire.
    const loadDocHistory = React.useCallback(() => {
        moduleApi.getDocumentRequests().then((d: any) => setDocHistory(Array.isArray(d) ? d : [])).catch(() => setDocHistory([]));
    }, []);

    React.useEffect(() => { loadDocHistory(); }, [loadDocHistory]);

    React.useEffect(() => {
        Promise.all([moduleApi.getProRdvSettings().catch(() => null), moduleApi.getEmployes().catch(() => [])]).then(([settings, emps]: any[]) => {
            const eligibleIds: string[] = Array.isArray(settings?.eligible_employee_ids) ? settings.eligible_employee_ids.map(String) : [];
            const allEmps = Array.isArray(emps) ? emps : [];
            setEmployees(eligibleIds.length > 0 ? allEmps.filter((e: any) => eligibleIds.includes(String(e.id))) : allEmps);
        }).catch(() => setEmployees([]));
    }, []);

    const apptsByDay = React.useMemo(() => {
        const map: Record<string, RdvAppointment[]> = {};
        appointments.forEach((a) => {
            if (!map[a.appointment_date]) map[a.appointment_date] = [];
            map[a.appointment_date].push(a);
        });
        Object.values(map).forEach((list) => list.sort((a, b) => a.start_time.localeCompare(b.start_time)));
        return map;
    }, [appointments]);

    const openCreate = (date: string) => { setEditing(null); setDefaultDate(date); setShowForm(true); };
    const openDetail = (appt: RdvAppointment) => setShowDetail(appt);
    const openEditFromDetail = () => { if (showDetail) { setEditing(showDetail); setDefaultDate(showDetail.appointment_date); setShowDetail(null); setShowForm(true); } };
    const closeForm = () => setShowForm(false);
    const afterChange = () => { setShowForm(false); setShowDetail(null); load(); loadPending(); };
    const handlePendingClick = (p: RdvAppointment) => {
        setCurrentDate(new Date(p.appointment_date + 'T00:00:00'));
        setShowPendingList(false);
        setShowDetail(p);
    };

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><CalendarCheck size={24} /> RDV Pro</h1>
                    <p className="text-content-muted mt-1">Rendez-vous pris par vos contacts, ou programmés manuellement.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <button onClick={() => setShowPendingList((v) => !v)}
                            className="relative p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                            <Bell size={18} />
                            {(pending.length + recentlyConfirmed.length) > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">
                                    {(pending.length + recentlyConfirmed.length) > 9 ? '9+' : pending.length + recentlyConfirmed.length}
                                </span>
                            )}
                        </button>
                        {showPendingList && (
                            <div className="absolute right-0 mt-2 w-80 bg-surface border border-border rounded-2xl shadow-2xl z-40 max-h-[28rem] overflow-y-auto">
                                {recentlyConfirmed.length > 0 && (
                                    <>
                                        <p className="px-3 pt-3 pb-1.5 text-[10px] font-bold text-content-muted uppercase tracking-widest">RDV confirmés récemment</p>
                                        {recentlyConfirmed.map((p) => (
                                            <button key={p.id} onClick={() => handlePendingClick(p)}
                                                className="w-full text-left p-3 border-b border-border last:border-0 hover:bg-surface-soft transition-all">
                                                <p className="text-sm font-bold text-content flex items-center gap-1.5"><CalendarCheck size={12} className="text-emerald-500 shrink-0" /> {contactLabel(p)}</p>
                                                <p className="text-xs text-content-muted">{new Date(p.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à {p.start_time}</p>
                                                {p.document_name && <p className="text-[11px] text-content-muted truncate">{p.document_name}</p>}
                                            </button>
                                        ))}
                                    </>
                                )}
                                <p className="px-3 pt-3 pb-1.5 text-[10px] font-bold text-content-muted uppercase tracking-widest border-t border-border">Demandes de RDV en attente</p>
                                {pending.length === 0 ? (
                                    <p className="p-3 text-xs text-content-muted">Aucune demande en attente.</p>
                                ) : pending.map((p) => (
                                    <button key={p.id} onClick={() => handlePendingClick(p)}
                                        className="w-full text-left p-3 border-b border-border last:border-0 hover:bg-surface-soft transition-all">
                                        <p className="text-sm font-bold text-content">{contactLabel(p)}</p>
                                        <p className="text-xs text-content-muted">{new Date(p.appointment_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à {p.start_time}</p>
                                        {p.subject && <p className="text-[11px] text-content-muted truncate">{p.subject}</p>}
                                    </button>
                                ))}
                                <p className="px-3 pt-3 pb-1.5 text-[10px] font-bold text-content-muted uppercase tracking-widest border-t border-border">Démarches envoyées</p>
                                {docHistory.length === 0 ? (
                                    <p className="p-3 text-xs text-content-muted">Aucune démarche envoyée.</p>
                                ) : docHistory.slice(0, 8).map((d) => (
                                    <div key={d.id} className="p-3 border-b border-border last:border-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-bold text-content truncate">{d.first_name || d.last_name ? `${d.first_name || ''} ${d.last_name || ''}`.trim() : (d.contact_company_name || 'Contact')}</p>
                                            <span className="shrink-0 text-[9px] font-black uppercase px-1.5 py-0.5 rounded border border-border text-content-muted">
                                                {d.status === 'completed' ? 'Terminé' : d.status === 'responded' ? 'Paiement en attente' : 'En attente'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-content-muted truncate">{d.template_name} — {new Date(d.sent_at).toLocaleDateString('fr-FR')}</p>
                                    </div>
                                ))}
                                <button onClick={() => { setShowPendingList(false); navigate('/demandes-recurrentes'); }}
                                    className="w-full text-center p-2.5 text-xs font-bold text-brand-600 dark:text-brand-400 hover:bg-surface-soft transition-all">
                                    Voir tout l'historique
                                </button>
                            </div>
                        )}
                    </div>
                    <button onClick={() => navigate('/rdv-pro/annulations')}
                        className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all" title="Historique des annulations">
                        <CalendarX2 size={18} />
                    </button>
                    <button onClick={() => setShowSettings(true)}
                        className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all" title="Paramètres RDV Pro">
                        <SettingsIcon size={18} />
                    </button>
                    <button onClick={() => setShowSendDoc(true)}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                        <FileText size={16} /> Envoyer une documentation
                    </button>
                    <button onClick={() => openCreate(format(new Date(), 'yyyy-MM-dd'))}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold transition-all">
                        <Plus size={16} /> Nouveau RDV
                    </button>
                </div>
            </header>

            <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <button onClick={() => setCurrentDate((d) => subMonths(d, 1))} className="p-2 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronLeft size={16} /></button>
                    <h2 className="text-sm font-bold text-content capitalize">{format(currentDate, 'MMMM yyyy', { locale: fr })}</h2>
                    <button onClick={() => setCurrentDate((d) => addMonths(d, 1))} className="p-2 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronRight size={16} /></button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
                ) : (
                    <div className="grid grid-cols-7">
                        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
                            <div key={d} className="text-center text-[10px] font-bold text-content-muted uppercase tracking-widest py-2 border-b border-border">{d}</div>
                        ))}
                        {days.map((day) => {
                            const key = format(day, 'yyyy-MM-dd');
                            const dayAppts = (apptsByDay[key] || []);
                            const visible = dayAppts.filter((a) => a.status === 'confirme' || a.status === 'en_attente');
                            const inMonth = isSameMonth(day, currentDate);
                            return (
                                <div key={key}
                                    className={`min-h-[110px] border-b border-r border-border p-1.5 space-y-1 ${inMonth ? '' : 'opacity-40'} hover:bg-surface-soft/50 transition-colors group relative`}>
                                    <div className="flex items-center justify-between">
                                        <span className={`text-xs font-bold ${isToday(day) ? 'w-5 h-5 rounded-full bg-brand-600 text-brand-fg flex items-center justify-center' : 'text-content-muted'}`}>{format(day, 'd')}</span>
                                        <button onClick={() => openCreate(key)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-content-muted hover:text-brand-600 transition-all"><Plus size={12} /></button>
                                    </div>
                                    {visible.slice(0, 3).map((a) => {
                                        const assignedNames = employees.filter((e) => a.employee_ids.includes(String(e.id))).map(employeeName);
                                        const choicesLabel = (a.response_choices || []).map((c) => `${c.label}${c.qty > 1 ? ` x${c.qty}` : ''}`).join(', ');
                                        const answersLabel = (a.custom_answers || []).map((q) => q.answer).join(', ');
                                        const summaryLabel = choicesLabel || answersLabel || a.subject;
                                        return (
                                            <button key={a.id} onClick={() => openDetail(a)}
                                                className={`w-full text-left px-1.5 py-1 rounded-lg border text-[10px] font-bold transition-all leading-tight space-y-0.5 ${a.status === 'en_attente' ? 'bg-warning-500/10 hover:bg-warning-500/20 border-warning-500/20 text-warning-500' : 'bg-brand-500/10 hover:bg-brand-500/20 border-brand-500/20 text-brand-600 dark:text-brand-400'}`}>
                                                <span className="flex items-center justify-between gap-1">
                                                    <span className="flex items-center gap-1"><Clock size={9} />{a.start_time}–{a.end_time}</span>
                                                    {Number(a.total_amount_ttc) > 0 && (
                                                        <span className={`shrink-0 px-1 rounded ${(Number(a.amount_already_paid) || 0) >= Number(a.total_amount_ttc) ? 'text-emerald-500' : (Number(a.amount_already_paid) || 0) > 0 ? 'text-warning-500' : 'text-content-muted'}`}>
                                                            {(Number(a.amount_already_paid) || 0) >= Number(a.total_amount_ttc) ? 'Payé' : (Number(a.amount_already_paid) || 0) > 0 ? 'Acompte' : 'À encaisser'}
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="block break-words">{contactLabel(a)}</span>
                                                {summaryLabel && (
                                                    <span className="block break-words font-normal opacity-80">{summaryLabel}</span>
                                                )}
                                                {assignedNames.length > 0 && <span className="block break-words font-normal opacity-80">{assignedNames.join(', ')}</span>}
                                                {assignedNames.length === 0 && a.assigned_note && <span className="block break-words font-normal opacity-80">{a.assigned_note}</span>}
                                            </button>
                                        );
                                    })}
                                    {visible.length > 3 && <p className="text-[10px] text-content-muted font-bold px-1.5">+{visible.length - 3} autre{visible.length - 3 > 1 ? 's' : ''}</p>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {showDetail && (
                <RdvDetailModal appt={showDetail} employees={employees} onClose={() => setShowDetail(null)} onEdit={openEditFromDetail} onCancelled={afterChange} onDecided={afterChange} />
            )}
            {showForm && (
                <RdvFormModal initial={editing} defaultDate={defaultDate} employees={employees} onClose={closeForm} onSaved={afterChange} />
            )}
            {showSendDoc && (
                <SendDocumentByEmailModal onClose={() => setShowSendDoc(false)} onSent={() => setShowSendDoc(false)} />
            )}
            {showSettings && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <h2 className="text-base font-bold text-content flex items-center gap-2"><SettingsIcon size={17} className="text-brand-500" /> Paramètres RDV Pro</h2>
                            <button onClick={() => setShowSettings(false)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto">
                            <ProRdvHoursTab />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
